/* eslint-disable react/prop-types */
/* eslint-disable no-unused-vars */
import { Box, Skeleton } from '@mui/material';
import {
  Portlet,
  PortletContent,
  PortletHeader,
  PortletLabel,
  PortletToolbar,
} from 'components';
import React, { useRef, useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { useIntl } from 'react-intl';
import theme from 'theme';
import { Download, ShareButton } from 'components/Download';

// Registrar los componentes necesarios de Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

export const Dashboard = Object.freeze({
  group: 'group',
  individual: 'individual',
});

export default function ComparativeChart({
  title,
  year,
  comparativeYearConsume,
  dashboard,
}) {
  const intl = useIntl();
  const chartRef = useRef();
  const [iconHide, setIconHide] = useState(false);

  /**
   * Lista de los nombres de los meses, traducidos según el idioma actual.
   */
  const months = [
    intl.formatMessage({ id: 'app.months.jan' }),
    intl.formatMessage({ id: 'app.months.feb' }),
    intl.formatMessage({ id: 'app.months.mar' }),
    intl.formatMessage({ id: 'app.months.apr' }),
    intl.formatMessage({ id: 'app.months.may' }),
    intl.formatMessage({ id: 'app.months.jun' }),
    intl.formatMessage({ id: 'app.months.jul' }),
    intl.formatMessage({ id: 'app.months.ago' }),
    intl.formatMessage({ id: 'app.months.sep' }),
    intl.formatMessage({ id: 'app.months.oct' }),
    intl.formatMessage({ id: 'app.months.nov' }),
    intl.formatMessage({ id: 'app.months.dec' }),
  ];

  /**
   * Configuración de los datasets, cada uno representando un año diferente.
   */
  const datasets = [
    {
      label: `${year - 3}`,
      borderColor: '#22B14C',
      backgroundColor: '#22B14C',
      data:
        dashboard === Dashboard.group
          ? comparativeYearConsume
            ? comparativeYearConsume?.year_3?.months_consumes
            : []
          : comparativeYearConsume?.year_3 || [],
    },
    {
      label: `${year - 2}`,
      borderColor: '#FF7F27',
      backgroundColor: '#FF7F27',
      data:
        dashboard === Dashboard.group
          ? comparativeYearConsume
            ? comparativeYearConsume?.year_2?.months_consumes
            : []
          : comparativeYearConsume?.year_2 || [],
    },
    {
      label: `${year - 1}`,
      borderColor: '#0080BC',
      backgroundColor: '#0080BC',
      data:
        dashboard === Dashboard.group
          ? comparativeYearConsume
            ? comparativeYearConsume?.year_1?.months_consumes
            : []
          : comparativeYearConsume?.year_1 || [],
    },
    {
      label: `${year}`,
      borderColor: '#C69100',
      backgroundColor: '#C69100',
      data:
        dashboard === Dashboard.group
          ? comparativeYearConsume
            ? comparativeYearConsume?.actual_year?.months_consumes
            : []
          : comparativeYearConsume?.actual_year || [],
    },
  ];

  /**
   * Configuración de los datos del gráfico.
   */
  const data = { labels: months, datasets };

  /**
   * Opciones de configuración del gráfico.
   */
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: true },
      tooltip: {
        enabled: true,
        mode: 'index',
        intersect: false,
        callbacks: {
          label: (tooltipItem) => {
            const dataset = datasets[tooltipItem.datasetIndex];
            const value = tooltipItem.raw.toLocaleString(); // Formatea con comas
            return `${dataset.label}: ${value} kWh`;
          },
        },
      },
    },
    scales: {
      y: {
        ticks: {
          callback: (value) => value.toLocaleString(), // Formatea valores del eje Y con comas
        },
      },
    },
  };

  return (
    <div ref={chartRef} style={{ height: '100%' }}>
      <Portlet style={{ height: '100%' }}>
        <PortletHeader noDivider>
          {title ? (
            <PortletLabel title={title} />
          ) : (
            <Skeleton variant="text" width={200} height={50} />
          )}
          <PortletToolbar style={{ display: 'flex', gap: 8 }}>
            <Download itemRef={chartRef} name={title} />
            <ShareButton itemRef={chartRef} name={title} />
          </PortletToolbar>
        </PortletHeader>
        <PortletContent
          style={{
            paddingLeft: theme.spacing(1),
            paddingRight: theme.spacing(1),
          }}
        >
          {comparativeYearConsume ? (
            <Box height={450} position="relative">
              <Line data={data} options={options} />
            </Box>
          ) : (
            <Skeleton variant="rectangular" height={400} />
          )}
        </PortletContent>
      </Portlet>
    </div>
  );
}
